// Audience pages. First-draft wording for Bern's approval; text corrections come later.
// `page` values are page keys used in App.js; 'contact' scrolls to the Home contact form.
// Process shown to farmers: learn first, then insure, then borrow, then sell.
// Imfuyo does not lend on its own: loans are made with licensed lending partners (co-lending).
// Imfuyo co-underwrites with licensed insurance companies and advises them on agricultural data and financial models.
export const AUDIENCES = {
  farmers: {
    eyebrow: 'For farmers',
    title: 'Learn, protect and grow your herd.',
    sub: 'Train first, insure next, borrow when you are ready. One record on the Imfuyo Platform carries you through every step.',
    ctas: [
      { label: 'Start learning', page: 'academy' },
      { label: 'Protect your herd', page: 'insurance' },
    ],
    benefits: [
      { title: 'Learn first', text: 'Training in livestock farming, record keeping and financial planning, through MbuziPlus and Imfuyo Academy.' },
      { title: 'Insure next', text: 'Livestock insurance from licensed insurance companies, supported by your verified animal record.' },
      { title: 'Borrow when ready', text: 'Loans made with licensed lending partners, based on your records and your insurance.' },
    ],
    steps: [
      { title: 'Learn', text: 'Complete training and register your farm.' },
      { title: 'Insure', text: 'Tag and record your animals, then insure the herd.' },
      { title: 'Borrow and sell', text: 'Apply for a loan with our partners and reach buyers.' },
    ],
    links: [
      { title: 'MbuziPlus', text: 'The dairy goat programme.', page: 'mbuziplus' },
      { title: 'Imfuyo Academy', text: 'The MbuziPlus Course: 42 modules, free preview.', page: 'academy' },
      { title: 'Financial literacy', text: 'Planning, records and savings.', page: 'whatwedo' },
      { title: 'Livestock insurance', text: 'Apply for cover or file a claim.', page: 'insurance' },
      { title: 'Farmer loans', text: 'Apply with our lending partners.', page: 'loans' },
      { title: 'Market Place', text: 'Tags, tools, feed and training.', page: 'shop' },
    ],
    livestock: { title: 'Livestock we work with.', text: 'Cows (dairy and beef), goats, pigs and camels can be recorded and insured through Imfuyo. MbuziPlus is our dairy goat programme.' },
    caveat: { title: 'Who does what.', text: 'Imfuyo runs the platform and works with licensed insurance companies and lenders. Insurers provide cover and lenders provide loans, together with Imfuyo.' },
  },

  groups: {
    eyebrow: 'For farmer groups',
    title: 'Farm as one. Learn, insure and sell together.',
    sub: 'Whether you keep dairy animals, meat animals or both, and whether you are a self-help group, a chama or a livestock group, one shared record lets every member be seen. Train, protect your herds and reach buyers together.',
    ctas: [
      { label: 'Register your group', page: 'register-group' },
      { label: 'Partner with Imfuyo', page: 'contact' },
    ],
    benefits: [
      { title: 'One group record', text: 'Members and herds in one place, with each member in control of their own information.' },
      { title: 'Train together', text: 'Group training through MbuziPlus and Imfuyo Academy.' },
      { title: 'Stronger together', text: 'Group cover, loans with our lending partners, and direct access to buyers.' },
    ],
    steps: [
      { title: 'Register the group', text: 'Group details and members, sent safely to Imfuyo.' },
      { title: 'Train and record', text: 'Learn together and record each member\'s animals.' },
      { title: 'Insure, borrow and sell', text: 'Cover first, then credit with partners, then markets.' },
    ],
    links: [
      { title: 'MbuziPlus', text: 'Train and farm together.', page: 'mbuziplus' },
      { title: 'Imfuyo Academy', text: 'The MbuziPlus Course for your group, with group keys.', page: 'academy' },
      { title: 'Livestock insurance', text: 'Cover for members\' herds.', page: 'insurance' },
      { title: 'Farmer loans', text: 'Apply with our lending partners.', page: 'loans' },
      { title: 'Market Place', text: 'Buy and sell as a group.', page: 'shop' },
    ],
    livestock: { title: 'Livestock we work with.', text: 'Cows (dairy and beef), goats, pigs and camels can be recorded and insured through Imfuyo. MbuziPlus is our dairy goat programme.' },
    caveat: { title: 'Your information stays yours.', text: 'Each member decides what is shared, and with whom.' },
  },

  cooperatives: {
    eyebrow: 'For cooperatives',
    title: 'Know every member. Serve every herd.',
    sub: 'A cooperative sees its members, their animals and their production in one record, and can offer training, cover, credit and markets to every member.',
    ctas: [
      { label: 'Partner with Imfuyo', page: 'contact' },
      { label: 'How the platform works', page: 'whatwedo' },
    ],
    benefits: [
      { title: 'Member records', text: 'Every member and animal recorded and verified in one place.' },
      { title: 'Better services', text: 'Connect members to training, insurance companies, lending partners and buyers.' },
      { title: 'Reliable supply', text: 'Traceable animals and dependable production information.' },
    ],
    steps: [
      { title: 'Onboard members', text: 'Register members and their herds.' },
      { title: 'Train and record', text: 'Members learn, and production and sales join the record.' },
      { title: 'Offer services', text: 'Cover, then credit, then markets for members.' },
    ],
    links: [
      { title: 'What we do', text: 'How the Imfuyo Platform works.', page: 'whatwedo' },
      { title: 'Livestock insurance', text: 'Cover for members.', page: 'insurance' },
      { title: 'Farmer loans', text: 'Credit with our lending partners.', page: 'loans' },
      { title: 'Market Place', text: 'Tags, tools, feed and training.', page: 'shop' },
    ],
    livestock: { title: 'Livestock we work with.', text: 'Cows (dairy and beef), goats, pigs and camels can be recorded and insured through Imfuyo. MbuziPlus is our dairy goat programme.' },
    caveat: { title: 'Consent first.', text: 'Member information is shared only with the member\'s permission.' },
  },

  insurers: {
    eyebrow: 'For insurance companies',
    title: 'Insure livestock with confidence.',
    sub: 'Verified animals, owners and values, so cover is priced fairly and claims are settled faster. We co-underwrite with you and advise on agricultural data and financial models.',
    ctas: [
      { label: 'Partner with Imfuyo', page: 'contact' },
      { label: 'How we work', page: 'whatwedo' },
    ],
    benefits: [
      { title: 'Verified records', text: 'Every animal identified, valued and linked to its owner, with the owner\'s consent.' },
      { title: 'Co-underwriting', text: 'We work alongside your underwriters to design and place livestock cover.' },
      { title: 'Advisory', text: 'Data and financial models focused on agriculture, shared with your teams.' },
    ],
    steps: [
      { title: 'Connect', text: 'Agree data sharing and the working arrangement.' },
      { title: 'Design', text: 'Co-create the product with your team.' },
      { title: 'Underwrite and settle', text: 'Underwrite together; your company pays valid claims.' },
    ],
    links: [
      { title: 'What we do', text: 'How the Imfuyo Platform works.', page: 'whatwedo' },
      { title: 'Farmer view', text: 'What farmers see when they apply.', page: 'insurance' },
      { title: 'Impact', text: 'Projects and evidence.', page: 'impact' },
    ],
    caveat: { title: 'Who does what.', text: 'Your company remains the licensed insurer. Imfuyo co-underwrites with you, supplies verified data and advises on agricultural data and financial models.' },
  },

  lenders: {
    eyebrow: 'For lenders',
    title: 'Lend against livestock you can verify.',
    sub: 'Verified animals, production records and insurance status, so you can assess a livestock enterprise with confidence. We co-lend with you and advise on agricultural data and financial models.',
    ctas: [
      { label: 'Partner with Imfuyo', page: 'contact' },
      { label: 'How we work', page: 'whatwedo' },
    ],
    benefits: [
      { title: 'Verified and insured', text: 'Animals identified, valued and, where possible, insured before a loan is considered.' },
      { title: 'Co-lending', text: 'Imfuyo works alongside you as a co-lending partner. Imfuyo does not lend on its own.' },
      { title: 'Advisory', text: 'Data and financial models focused on agriculture, shared with your teams.' },
    ],
    steps: [
      { title: 'Connect', text: 'Agree data sharing and the working arrangement.' },
      { title: 'Review', text: 'See applications supported by records and insurance.' },
      { title: 'Co-lend', text: 'Decide and lend together; your institution holds the loan.' },
    ],
    links: [
      { title: 'What we do', text: 'How the Imfuyo Platform works.', page: 'whatwedo' },
      { title: 'Farmer view', text: 'What farmers see when they apply.', page: 'loans' },
      { title: 'Impact', text: 'Projects and evidence.', page: 'impact' },
    ],
    caveat: { title: 'Who does what.', text: 'Your institution remains the licensed lender. Imfuyo co-lends with partners and advises on agricultural data and financial models.' },
  },

  vets: {
    eyebrow: 'For veterinary offices',
    title: 'Your records, trusted across the value chain.',
    sub: 'Log visits, confirm animal health and offer your services to farmers through the Imfuyo Platform.',
    ctas: [
      { label: 'Join as a veterinary office', page: 'contact' },
      { label: 'How the platform works', page: 'whatwedo' },
    ],
    benefits: [
      { title: 'Log visits', text: 'Treatments, vaccinations and checks recorded against each animal.' },
      { title: 'Confirm health', text: 'Health records that support insurance and lending decisions.' },
      { title: 'Reach farmers', text: 'Offer your services to farmers, groups and cooperatives.' },
    ],
    steps: [
      { title: 'Register', text: 'Tell us about your practice.' },
      { title: 'Connect', text: 'We agree how you add to the record.' },
      { title: 'Serve', text: 'Record visits and serve farmers.' },
    ],
    links: [
      { title: 'What we do', text: 'How the Imfuyo Platform works.', page: 'whatwedo' },
      { title: 'Imfuyo Academy', text: 'Training for the value chain.', page: 'academy' },
      { title: 'Impact', text: 'Projects and evidence.', page: 'impact' },
    ],
    caveat: { title: 'Professional standards.', text: 'Your clinical judgement stays yours. Records are shared only with the farmer\'s permission.' },
  },

  buyers: {
    eyebrow: 'For buyers and processors',
    title: 'Source traceable animals with confidence.',
    sub: 'Reach farmers, groups and cooperatives with identified animals and dependable supply.',
    ctas: [
      { label: 'Partner with Imfuyo', page: 'contact' },
      { label: 'Visit the Market Place', page: 'shop' },
    ],
    benefits: [
      { title: 'Traceable animals', text: 'Each animal identified and linked to its owner and history.' },
      { title: 'Dependable supply', text: 'Plan purchases with groups and cooperatives.' },
      { title: 'Direct trade', text: 'Direct links between farmers and buyers, without exploitative middlemen.' },
    ],
    steps: [
      { title: 'Register', text: 'Tell us what you buy.' },
      { title: 'Connect', text: 'Meet farmers, groups and cooperatives.' },
      { title: 'Buy', text: 'Purchase traceable animals.' },
    ],
    links: [
      { title: 'Market Place', text: 'Tags, tools, feed and training.', page: 'shop' },
      { title: 'Farmer groups', text: 'Groups that sell together.', page: 'groups' },
      { title: 'Cooperatives', text: 'Members and their herds.', page: 'cooperatives' },
    ],
    caveat: { title: 'Fair trade.', text: 'Every sale is recorded, so farmers and buyers both have a clear history.' },
  },

  governments: {
    eyebrow: 'For governments and universities',
    title: 'Standard livestock data for policy and research.',
    sub: 'Consistent, consented information that supports planning, research and training across the livestock value chain.',
    ctas: [
      { label: 'Partner with Imfuyo', page: 'contact' },
      { label: 'See our impact', page: 'impact' },
    ],
    benefits: [
      { title: 'Policy', text: 'Consistent information to plan and measure livestock programmes.' },
      { title: 'Research', text: 'Structured data for universities and research partners.' },
      { title: 'Training', text: 'Shared curricula through Imfuyo Academy.' },
    ],
    steps: [
      { title: 'Register', text: 'Tell us your mandate.' },
      { title: 'Agree', text: 'We agree how information is shared and protected.' },
      { title: 'Contribute', text: 'Use the data for policy, research and training.' },
    ],
    links: [
      { title: 'What we do', text: 'How the Imfuyo Platform works.', page: 'whatwedo' },
      { title: 'Impact', text: 'Projects and evidence.', page: 'impact' },
      { title: 'About', text: 'Who we are and who we work with.', page: 'about' },
    ],
    caveat: { title: 'Consent and protection.', text: 'Information is shared only with permission. Imfuyo is registered with Kenya\'s Office of the Data Protection Commissioner.' },
  },
};
